import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useBooks } from '../context/BooksContext';
import { IoSearch, IoFlame, IoTime, IoHeart, IoBook, IoOptions, IoClose, IoStar } from 'react-icons/io5';
import StarRating from '../components/StarRating';
import { estimateBookReadingTime, formatReadingTime } from '../utils/readingTime';
import { SkeletonGrid } from '../components/Skeleton';
import { doc, getDoc } from 'firebase/firestore';
import SEO from '../components/SEO';
import AdBanner from '../components/AdBanner';
import { db } from '../firebase';
import './Explore.css';

const GENRES = [
  { id: 'all', label: 'Tous', emoji: '📚' },
  { id: 'romance', label: 'Romance', emoji: '💕' },
  { id: 'fantasy', label: 'Fantasy', emoji: '🗡️' },
  { id: 'thriller', label: 'Thriller', emoji: '🔍' },
  { id: 'scifi', label: 'Sci-Fi', emoji: '🚀' },
  { id: 'drama', label: 'Drame', emoji: '🎭' },
  { id: 'horror', label: 'Horreur', emoji: '👻' },
  { id: 'humor', label: 'Humour', emoji: '😂' },
  { id: 'poetry', label: 'Poésie', emoji: '✨' },
  { id: 'adventure', label: 'Aventure', emoji: '🧭' },
  { id: 'other', label: 'Autre', emoji: '📖' },
];

const SORT_OPTIONS = [
  { id: 'popular', label: 'Populaire', icon: IoFlame },
  { id: 'recent', label: 'Récent', icon: IoTime },
  { id: 'liked', label: 'Aimés', icon: IoHeart },
  { id: 'rated', label: 'Notés', icon: IoStar },
];

export default function Explore() {
  const { books: allBooks, loading } = useBooks();
  const books = allBooks.filter(b => b.status !== 'draft' && !b.hidden);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [showFilters, setShowFilters] = useState(false);
  const [minRating, setMinRating] = useState(0);
  const [searchBy, setSearchBy] = useState('all');
  const [collections, setCollections] = useState([]);

  // Charger les collections depuis Firestore
  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const colDoc = await getDoc(doc(db, 'config', 'collections'));
        if (colDoc.exists() && colDoc.data().list) {
          setCollections(colDoc.data().list.filter(c => c.active !== false));
        }
      } catch (e) {}
    };
    fetchCollections();
  }, []);

  // Livres boostés actifs
  const boostedBooks = useMemo(() => {
    const now = new Date().toISOString();
    return books.filter(b => b.boosted && b.boostUntil && b.boostUntil > now)
      .sort((a, b) => (b.boostLevel || 0) - (a.boostLevel || 0));
  }, [books]);

  const getAverageRating = (book) => {
    const ratings = book.ratings ? Object.values(book.ratings) : [];
    if (ratings.length === 0) return 0;
    return ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
  };

  const filtered = useMemo(() => {
    let result = [...books];

    if (selectedGenre !== 'all') {
      result = result.filter(b => b.genre === selectedGenre);
    }

    if (minRating > 0) {
      result = result.filter(b => getAverageRating(b) >= minRating);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(b => {
        switch (searchBy) {
          case 'title': return b.title.toLowerCase().includes(q);
          case 'author': return b.author.toLowerCase().includes(q);
          case 'tag': return b.tags && b.tags.some(t => t.toLowerCase().includes(q));
          default:
            return b.title.toLowerCase().includes(q) ||
              b.author.toLowerCase().includes(q) ||
              (b.synopsis || '').toLowerCase().includes(q) ||
              (b.tags && b.tags.some(t => t.toLowerCase().includes(q)));
        }
      });
    }

    switch (sortBy) {
      case 'recent': result.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')); break;
      case 'liked': result.sort((a, b) => (b.likes || 0) - (a.likes || 0)); break;
      case 'rated': result.sort((a, b) => getAverageRating(b) - getAverageRating(a)); break;
      default: result.sort((a, b) => ((b.likes || 0) + (b.views || 0) / 10) - ((a.likes || 0) + (a.views || 0) / 10));
    }

    return result;
  }, [books, searchQuery, selectedGenre, sortBy, minRating, searchBy]);

  const getCollectionBooks = (col) => {
    let result = [...books];

    // Filtre par genre
    if (col.genre && col.genre !== 'all') {
      result = result.filter(b => b.genre === col.genre);
    }

    // Filtre par IDs spécifiques (sélection manuelle)
    if (col.bookIds && col.bookIds.length > 0) {
      result = result.filter(b => col.bookIds.includes(b.id));
    }

    // Tri
    if (col.sort === 'likes') result.sort((a, b) => (b.likes || 0) - (a.likes || 0));
    else if (col.sort === 'recent') result.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
    else if (col.sort === 'views') result.sort((a, b) => (b.views || 0) - (a.views || 0));

    return result.slice(0, col.maxBooks || 10);
  };

  const genreLabels = { romance: 'Romance', fantasy: 'Fantasy', thriller: 'Thriller', scifi: 'Sci-Fi', drama: 'Drame', horror: 'Horreur', humor: 'Humour', poetry: 'Poésie', adventure: 'Aventure', other: 'Autre' };

  const isSearching = searchQuery.trim() || selectedGenre !== 'all' || minRating > 0;

  return (
    <div className="page explore-page">
      <SEO title="Explorer" description="Explore des milliers de livres par genre, tendance et nouveauté sur Plumly." />
      <div className="explore-header">
        <h1 className="explore-title">Explorer</h1>
        <p className="explore-count">{filtered.length} livre{filtered.length > 1 ? 's' : ''}</p>
      </div>

      {/* Barre de recherche */}
      <div className="explore-search">
        <IoSearch className="explore-search-icon" />
        <input
          className="explore-search-input"
          placeholder="Rechercher un titre, auteur, tag..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        <motion.button
          className={'explore-filter-btn ' + (showFilters ? 'explore-filter-btn-active' : '')}
          onClick={() => setShowFilters(!showFilters)}
          whileTap={{ scale: 0.9 }}
        >
          {showFilters ? <IoClose /> : <IoOptions />}
        </motion.button>
      </div>

      {/* Filtres avancés */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            className="explore-advanced"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="explore-advanced-inner">
              <div className="explore-advanced-row">
                <label className="explore-advanced-label">Rechercher par</label>
                <div className="explore-advanced-options">
                  {[
                    { id: 'all', label: 'Tout' },
                    { id: 'title', label: 'Titre' },
                    { id: 'author', label: 'Auteur' },
                    { id: 'tag', label: 'Tag' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      className={'explore-adv-btn ' + (searchBy === opt.id ? 'explore-adv-btn-active' : '')}
                      onClick={() => setSearchBy(opt.id)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="explore-advanced-row">
                <label className="explore-advanced-label">Note minimum</label>
                <div className="explore-advanced-options">
                  {[0, 1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      className={'explore-adv-btn ' + (minRating === n ? 'explore-adv-btn-active' : '')}
                      onClick={() => setMinRating(n)}
                    >
                      {n === 0 ? 'Tous' : '⭐'.repeat(n)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Genres */}
      <div className="explore-genres">
        {GENRES.map(genre => (
          <button
            key={genre.id}
            className={'explore-genre ' + (selectedGenre === genre.id ? 'explore-genre-active' : '')}
            onClick={() => setSelectedGenre(genre.id)}
          >
            <span>{genre.emoji}</span> {genre.label}
          </button>
        ))}
      </div>

      {/* Livres boostés */}
      {!isSearching && boostedBooks.length > 0 && (
        <div className="explore-collections">
          <motion.div
            className="explore-collection explore-collection-boosted"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="explore-collection-header">
              <h3>🚀 Livres boostés</h3>
              <p>Mis en avant par la communauté</p>
            </div>
            <div className="explore-collection-scroll">
              {boostedBooks.map(book => (
                <motion.div
                  key={book.id}
                  className="explore-collection-card"
                  onClick={() => navigate('/book/' + book.id)}
                  whileHover={{ y: -4 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <div className="explore-collection-cover explore-collection-cover-boosted" style={{
                    background: book.coverImage
                      ? `url(${book.coverImage}) center/cover no-repeat`
                      : book.coverGradient || 'linear-gradient(135deg, #ccc, #999)'
                  }}>
                    <span className="explore-boost-badge">🚀</span>
                  </div>
                  <p className="explore-collection-title">{book.title}</p>
                  <p className="explore-collection-author">{book.author}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      )}

      {/* Collections — affichées uniquement quand pas de recherche active */}
      {!isSearching && collections.length > 0 && (
        <div className="explore-collections">
          {collections.map((col, ci) => {
            const colBooks = getCollectionBooks(col);
            if (colBooks.length === 0) return null;
            return (
              <motion.div
                key={col.id || ci}
                className="explore-collection"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: ci * 0.08 }}
              >
                <div className="explore-collection-header">
                  <h3>{col.emoji || '📚'} {col.title}</h3>
                  {col.subtitle && <p>{col.subtitle}</p>}
                </div>
                <div className="explore-collection-scroll">
                  {colBooks.map(book => (
                    <motion.div
                      key={book.id}
                      className="explore-collection-card"
            onClick={() => navigate('/book/' + book.id)}
                      whileHover={{ y: -4 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <div className="explore-collection-cover" style={{
                        background: book.coverImage
                          ? `url(${book.coverImage}) center/cover no-repeat`
                          : book.coverGradient || 'linear-gradient(135deg, #ccc, #999)'
                      }} />
                      <p className="explore-collection-title">{book.title}</p>
                      <p className="explore-collection-author">{book.author}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Pub */}
      <AdBanner slot="explore-mid" format="horizontal" />

      {/* Tri */}
      <div className="explore-sort">
        {SORT_OPTIONS.map(opt => {
          const Icon = opt.icon;
          return (
            <button
              key={opt.id}
              className={'explore-sort-btn ' + (sortBy === opt.id ? 'explore-sort-btn-active' : '')}
              onClick={() => setSortBy(opt.id)}
            >
              <Icon /> {opt.label}
            </button>
          );
        })}
      </div>

      {/* Résultats */}
      {loading && <SkeletonGrid count={6} />}
      <div className="explore-results">
        {filtered.length > 0 ? filtered.map((book, i) => (
          <motion.div
            key={book.id}
            className="explore-card"
            onClick={() => navigate('/book/' + book.id)}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}
          >
            <div className="explore-card-cover" style={{
              background: book.coverImage
                ? `url(${book.coverImage}) center/cover no-repeat`
                : book.coverGradient || 'linear-gradient(135deg, #2a2520, #1a1714)'
            }}>
              <span className={'tag tag-' + book.genre}>{genreLabels[book.genre] || book.genre}</span>
            </div>
            <div className="explore-card-body">
              <h3 className="explore-card-title">{book.title}</h3>
              <p className="explore-card-author" onClick={(e) => { e.stopPropagation(); navigate('/author/' + book.authorId); }}>
                par <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{book.author}</span>
              </p>
              <p className="explore-card-synopsis">{book.synopsis}</p>
              <div className="explore-card-footer">
                <StarRating bookId={book.id} ratings={book.ratings || {}} interactive={false} size="sm" />
                <span className="explore-card-stat"><IoHeart /> {(book.likes || 0).toLocaleString('fr-FR')}</span>
                <span className="explore-card-stat"><IoBook /> {(book.chapters || []).length} chap.</span>
                <span className="explore-card-stat">⏱ {formatReadingTime(estimateBookReadingTime(book.chapters || []))}</span>
              </div>
            </div>
          </motion.div>
        )) : (
          <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
            <div className="empty-state-icon">🔍</div>
            <p className="empty-state-text">Aucun livre trouvé. Essaie d'autres filtres !</p>
          </div>
        )}
      </div>
    </div>
  );
}